import { DEFAULT_SPORT, type Sport } from '@sharpr/types';
import type { TourStep, TourStepApi } from './types';

/** Resolve the sport the tour should run for. On a sport route we honor it so the
 *  first step lands on the page the user is already on; everywhere else (landing,
 *  settings, the account menu) we fall back to the schema's DEFAULT_SPORT rather
 *  than hardcoding a sport — so the tour's default tracks the app's default. */
export function tourSportFromPath(pathname: string | null | undefined): Sport {
  if (pathname?.startsWith('/mlb')) return 'mlb';
  if (pathname?.startsWith('/nba')) return 'nba';
  return DEFAULT_SPORT;
}

interface BuildTourStepsArgs {
  sport: Sport;
  /** When true, drops desktop-only steps (the sportsbook-column reorder, which
   *  needs the odds table that mobile doesn't render). */
  isMobile: boolean;
  /** Signed-in viewers without an active subscription or trial. Line movement
   *  is subscription-gated, so we mirror the signed-out variant of that step
   *  (no chart auto-open) with a "start your trial" lock copy. Defaults to
   *  true so existing callers behave as before. */
  hasAccess?: boolean;
}

/** The player-research portion of the walkthrough, shared by the signed-in and
 *  signed-out builders (the research card is public). It spotlights the research
 *  entry points on the card, then OPENS the real research overlay and walks a
 *  couple of signature tabs. For MLB it also highlights the card's lineup chip
 *  (both viewports) and weather pill (mobile only — the desktop card has no pill;
 *  weather there lives in the selected-players table) before opening the card.
 *
 *  Overlay steps carry NO targetId on purpose: the research drawer paints its own
 *  dim, so — like the line-movement step — the tour suppresses its own backdrop
 *  and floats the popover over the drawer (a spotlight hole can't reach inside a
 *  z-50 drawer from the z-9998 backdrop). Each card-spotlight step closes the
 *  overlay onEnter so stepping back out of the drawer doesn't leave it covering
 *  the spotlight; the final overlay step closes it before the tour moves on. */
function buildResearchSteps(sport: Sport, isMobile: boolean): TourStep[] {
  const sportPath = `/${sport}`;
  const isMlb = sport === 'mlb';
  const closeOverlays = (api: TourStepApi) => api.demoHandlers.closeOverlays?.();

  const steps: TourStep[] = [
    {
      id: 'player-research',
      route: sportPath,
      targetId: 'tour-demo-player-name',
      placement: 'right',
      title: 'Deep player research',
      body:
        'Click a player’s photo or name for a full research card — game logs, matchup data, and projections.',
      onEnter: closeOverlays,
    },
  ];

  // MLB-only card chips. Lineup first (left-panel card on desktop, same as the
  // player-research step), then weather. The weather pill lives on the mobile card
  // and in the desktop selected-players table — both stamped `tour-demo-weather`,
  // so the spotlight lands on whichever the current viewport shows.
  if (isMlb) {
    steps.push({
      id: 'lineup-chip',
      route: sportPath,
      targetId: 'tour-demo-lineup',
      placement: isMobile ? 'top' : 'right',
      title: 'Confirmed & projected lineups',
      body:
        'The lineup chip shows the batting order and whether it’s locked in yet. Tap it to see who’s in and where they hit.',
      onEnter: closeOverlays,
    });
    steps.push({
      id: 'weather-chip',
      route: sportPath,
      targetId: 'tour-demo-weather',
      placement: isMobile ? 'top' : 'right',
      title: 'Weather at a glance',
      body:
        'Outdoor games show a weather pill — wind, temperature, and conditions that move totals. Tap it for the full first-pitch forecast.',
      onEnter: closeOverlays,
    });
  }

  // Open the real research overlay (no targetId → the drawer shows through).
  steps.push({
    id: 'research-open',
    route: sportPath,
    placement: 'right',
    title: 'Inside the research card',
    body: isMlb
      ? 'Here’s the full card — it opens on the matchup: the opposing lineup, platoon splits, and the toughest threats to navigate.'
      : 'Here’s the full card — recent games charted against the line, plus matchup data and projections.',
    onEnter: (api: TourStepApi) => {
      api.demoHandlers.openResearchDemo?.();
      // MLB always opens on matchup; setting it explicitly also restores matchup
      // if the user steps back into this step from a later tab. (NBA opens on its
      // market tab via the overlay's own default — left as-is.)
      if (isMlb) api.demoHandlers.setResearchTab?.('matchup');
    },
  });

  // Signature tabs only — a quick walk, not every tab.
  const tabSteps: { id: string; tab: string; title: string; body: string }[] = isMlb
    ? [
        {
          id: 'research-history',
          tab: 'overview',
          title: 'Game logs & recent form',
          body: 'The History tab charts recent outings against the line, with a full game log underneath.',
        },
        {
          id: 'research-stuff',
          tab: 'arsenal',
          title: 'Pitch arsenal',
          body: 'Stuff breaks down the arsenal — velocity, movement, and usage for every pitch in the mix.',
        },
      ]
    : [
        {
          id: 'research-matchup',
          tab: 'matchup',
          title: 'Matchup breakdown',
          body: 'The matchup tab shows pace, defense, and how tonight’s opponent shapes the projection.',
        },
      ];

  tabSteps.forEach((t, i) => {
    const isLast = i === tabSteps.length - 1;
    steps.push({
      id: t.id,
      route: sportPath,
      placement: 'right',
      title: t.title,
      body: t.body,
      // Ensure the drawer is up, THEN switch the tab. On forward nav it's already open;
      // but on Back nav into this step (e.g. from Settings) the last tab step's onExit
      // already closed it, so setResearchTab alone would describe a hidden card. openResearchDemo
      // re-picks the same deterministic player (stable re-entry); repeat opens are tolerated
      // by design (raw analytics stream, dedup analyst-side).
      onEnter: (api: TourStepApi) => {
        api.demoHandlers.openResearchDemo?.();
        api.demoHandlers.setResearchTab?.(t.tab);
      },
      ...(isLast ? { onExit: closeOverlays } : {}),
    });
  });

  return steps;
}

/** Closing step: points at the How-To-Use guide and FAQ for a deeper dive. For
 *  signed-out viewers it leads with a start-trial CTA. No targetId — it floats as
 *  a wrap-up card. Links/CTA are full navigations, which tear down the tour. */
function buildLearnMoreStep(signedOut: boolean): TourStep {
  return {
    id: 'learn-more',
    placement: 'center',
    title: signedOut ? 'Start sharpening your edges' : 'You’re all set',
    body: signedOut
      ? 'That’s the tour. Start a free 5-day trial to unlock projections, EV, and line movement — or read the guide and FAQ first.'
      : 'That’s the tour. For a deeper dive on any feature, walk through the How-To-Use guide or browse common questions in the FAQ.',
    ...(signedOut ? { cta: { label: 'Start free trial', href: '/signup' } } : {}),
    links: [
      { label: 'How-to-use guide', href: '/how-to-use' },
      { label: 'FAQ', href: '/faq' },
    ],
  };
}

/** The default product walkthrough fired from the header Tour button. Ordering
 *  flows top-of-page → table interactions → settings, so each step builds on
 *  what the user has just seen. Sport-specific copy uses the URL/sport context,
 *  not these strings. */
export function buildSharprTourSteps({ sport, isMobile, hasAccess = true }: BuildTourStepsArgs): TourStep[] {
  const sportPath = `/${sport}`;
  const allSteps: TourStep[] = [
    {
      id: 'filters',
      route: sportPath,
      targetId: 'tour-filters',
      placement: 'right',
      title: 'Filter by team and market',
      body:
        sport === 'mlb'
          ? 'Pick teams and markets to focus on — the chips only show markets with live odds for tonight. Toggle “Confirmed lineup” to hide pitchers until the opposing lineup is confirmed.'
          : 'Pick teams and markets to focus on — everything else drops away. The market chips only show markets with live odds for tonight.',
    },
    {
      id: 'tour-filter-popover',
      route: sportPath,
      targetId: 'tour-demo-filter-button',
      // 'right' so the tour card sits in the odds-table area beside the
      // sidebar, clear of the auto-opened filter popover that drops below
      // the button.
      placement: 'right',
      title: 'Set a minimum EV and liquidity',
      body:
        'Click the filter icon to set a minimum EV floor and color the exchange-liquidity dot by dollar volume — see at a glance how liquid each price is.',
    },
    {
      id: 'sportsbook-column',
      route: sportPath,
      targetId: 'tour-demo-sportsbook-col',
      placement: 'bottom',
      title: 'Reorder sportsbook columns',
      body:
        'Drag any sportsbook column header left or right to put the books you actually bet at first. Order persists across sessions.',
    },
    // Mobile splits the line-movement story in two so the user sees the
    // dropdown reveal before the chart opens. Desktop keeps a single step that
    // auto-opens the chart over the spotlight. Signed-in viewers without
    // access fall back to the signed-out variant (no chart open) because the
    // line-movement endpoint is subscription-gated — copy points them at the
    // trial instead of "sign in".
    ...(isMobile
      ? [
          {
            id: 'line-movement-expand',
            route: sportPath,
            targetId: 'tour-demo-line-row',
            placement: 'top' as const,
            title: 'Per-book odds',
            body:
              'Tap the highlighted player card to expand it — you’ll see every book’s price and line side by side.',
            // No auto-expand: the spotlight is click-through, so the user
            // really taps the card. The next step catches up defensively if
            // they advance without tapping.
          },
          hasAccess
            ? {
                id: 'line-movement',
                route: sportPath,
                targetId: 'tour-demo-first-book',
                placement: 'top' as const,
                title: 'See how lines moved',
                body:
                  'Tap the highlighted book to open line movement, prop history, and an EV calculator — track how the price shifted and price up your own number.',
                // Defensive expand only — if the user advanced without tapping
                // the card on the previous step, ensure the per-book breakdown
                // (and its spotlight target) is in the DOM. We deliberately do
                // NOT auto-open the chart — the user's real tap on the spotlit
                // book button is the interaction we're teaching.
                onEnter: (api: TourStepApi) => api.demoHandlers.expandLineMovementDemoCard?.(),
                onExit: (api: TourStepApi) => {
                  api.demoHandlers.closeOverlays?.();
                  api.demoHandlers.collapseDemoCards?.();
                },
              }
            : {
                id: 'line-movement-locked',
                route: sportPath,
                targetId: 'tour-demo-first-book',
                placement: 'top' as const,
                title: 'See how lines moved',
                body:
                  'Tap any book to open line movement, prop history, and an EV calculator — see how the price shifted and price up your own number. Start your free trial to unlock it.',
                onEnter: (api: TourStepApi) => api.demoHandlers.expandLineMovementDemoCard?.(),
                onExit: (api: TourStepApi) => api.demoHandlers.collapseDemoCards?.(),
              },
        ]
      : [
          hasAccess
            ? {
                id: 'line-movement',
                route: sportPath,
                placement: 'right' as const,
                title: 'See how lines moved',
                body:
                  'Click any odds cell to open line movement, prop history, and an EV calculator — track how the price shifted and price up your own number.',
                onEnter: (api: TourStepApi) => api.demoHandlers.openLineMovementDemo?.(),
                onExit: (api: TourStepApi) => api.demoHandlers.closeOverlays?.(),
              }
            : {
                id: 'line-movement-locked',
                route: sportPath,
                targetId: 'tour-demo-odds-cell',
                placement: 'right' as const,
                title: 'See how lines moved',
                body:
                  'Click any odds cell to open line movement, prop history, and an EV calculator — see how the price shifted and price up your own number. Start your free trial to unlock it.',
              },
        ]),
    ...buildResearchSteps(sport, isMobile),
    {
      id: 'settings-nav',
      route: sportPath,
      targetId: 'tour-demo-settings-link',
      placement: 'left',
      title: 'Settings live in your account menu',
      body:
        'Open the menu in the top-right and click Settings — that’s where you configure your sportsbooks, hidden markets, and account.',
    },
    {
      id: 'settings-sportsbook',
      route: '/settings',
      targetId: 'tour-settings-sportsbook',
      placement: 'bottom',
      title: 'Pick which books to compare',
      body:
        'Set each book to Bet (eligible for best line), Reference (compare only), or Hide. Changes save automatically.',
    },
    {
      id: 'settings-markets',
      route: '/settings',
      targetId: 'tour-settings-markets',
      placement: 'top',
      title: 'Show only the markets you bet',
      body:
        'Hide markets you never bet on so the table stays focused on your edges. You can toggle them back on any time.',
    },
    buildLearnMoreStep(false),
  ];

  // Mobile drops the sportsbook-column step (no odds table = nothing to
  // reorder). Everything else applies to both viewports.
  return isMobile
    ? allSteps.filter((s) => s.id !== 'sportsbook-column')
    : allSteps;
}

/** Signed-out walkthrough variant. Never opens the line-movement overlay or
 *  navigates to /settings — both require auth — and instead spotlights where
 *  those features live, explaining what signing in would unlock. */
export function buildSignedOutSharprTourSteps({ sport, isMobile }: BuildTourStepsArgs): TourStep[] {
  const sportPath = `/${sport}`;
  const allSteps: TourStep[] = [
    {
      id: 'filters',
      route: sportPath,
      targetId: 'tour-filters',
      placement: 'right',
      title: 'Filter by team and market',
      body:
        sport === 'mlb'
          ? 'Pick teams and markets to focus on — the chips only show markets with live odds for tonight. Toggle “Confirmed lineup” to hide pitchers until the opposing lineup is confirmed.'
          : 'Pick teams and markets to focus on — everything else drops away. The market chips only show markets with live odds for tonight.',
    },
    {
      id: 'tour-filter-popover',
      route: sportPath,
      targetId: 'tour-demo-filter-button',
      placement: 'right',
      title: 'Set a minimum EV and liquidity',
      body:
        'Click the filter icon to set a minimum EV floor and color the exchange-liquidity dot by dollar volume — see at a glance how liquid each price is.',
    },
    {
      id: 'sportsbook-column',
      route: sportPath,
      targetId: 'tour-demo-sportsbook-col',
      placement: 'bottom',
      title: 'Reorder sportsbook columns',
      body:
        'Drag any sportsbook column header left or right to put the books you actually bet at first. Order persists across sessions.',
    },
    // Mobile splits this into "expand the card" → "spotlight the per-book
    // entry" so signed-out users see the same dropdown reveal as signed-in.
    ...(isMobile
      ? [
          {
            id: 'line-movement-expand',
            route: sportPath,
            targetId: 'tour-demo-line-row',
            placement: 'top' as const,
            title: 'Per-book odds',
            body:
              'Tap the highlighted player card to expand it — you’ll see every book’s price and line side by side.',
            // No auto-expand: spotlight is click-through, user really taps.
          },
          {
            id: 'line-movement-signed-out',
            route: sportPath,
            targetId: 'tour-demo-first-book',
            placement: 'top' as const,
            title: 'See how lines moved',
            body:
              'Tap any book to open line movement, prop history, and an EV calculator — see how the price shifted and price up your own number. Sign in to unlock it.',
            // Defensive expand if the user advanced without tapping the card.
            onEnter: (api: TourStepApi) => api.demoHandlers.expandLineMovementDemoCard?.(),
            onExit: (api: TourStepApi) => api.demoHandlers.collapseDemoCards?.(),
          },
        ]
      : [
          {
            id: 'line-movement-signed-out',
            route: sportPath,
            targetId: 'tour-demo-odds-cell',
            placement: 'right' as const,
            title: 'See how lines moved',
            body:
              'Click any odds cell to open line movement, prop history, and an EV calculator — see how the price shifted and price up your own number. Sign in to unlock it.',
          },
        ]),
    ...buildResearchSteps(sport, isMobile),
    {
      id: 'settings-signed-out',
      route: sportPath,
      targetId: 'tour-demo-account-area',
      placement: 'left',
      title: 'Your settings live here once you sign in',
      body:
        'After signing up, this corner becomes your account menu — pick which sportsbooks count toward best lines, hide markets you never bet, and manage your subscription. Start a free 5-day trial to try it.',
    },
    buildLearnMoreStep(true),
  ];

  return isMobile
    ? allSteps.filter((s) => s.id !== 'sportsbook-column')
    : allSteps;
}
